import { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import shippingZoneService from '../../services/shippingZoneService';
import Modal from '../../components/Common/Modal';
import Input from '../../components/Common/Input';
import Button from '../../components/Common/Button';

const ShippingZoneDialog = ({ isOpen, onClose, onSuccess, zone }) => {
  const { isRtl } = useLocalization();
  const isEdit = Boolean(zone);
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [price, setPrice] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNameAr(zone?.nameAr ?? '');
      setNameEn(zone?.nameEn ?? '');
      setPrice(zone ? String(zone.price) : '');
      setError('');
    }
  }, [isOpen, zone]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const priceValue = Number(price);
    if (price === '' || Number.isNaN(priceValue) || priceValue < 0) {
      setError(isRtl ? 'أدخل سعراً صحيحاً' : 'Enter a valid price');
      return;
    }
    if (!isEdit && (!nameAr.trim() || !nameEn.trim())) {
      setError(isRtl ? 'أدخل اسم المحافظة بالعربي والإنجليزي' : 'Enter the governorate name in Arabic and English');
      return;
    }

    try {
      setIsSaving(true);
      setError('');
      if (isEdit) {
        await shippingZoneService.updatePrice(zone.id, priceValue);
      } else {
        await shippingZoneService.create(nameEn.trim(), nameAr.trim(), priceValue);
      }
      onSuccess();
    } catch (err) {
      console.error('Error saving shipping zone:', err);
      const serverMessage = err.response?.data?.message || err.response?.data?.Message;
      setError(serverMessage || (isRtl ? 'حدث خطأ أثناء الحفظ' : 'Failed to save'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit
        ? (isRtl ? 'تعديل أجور التوصيل' : 'Edit Shipping Fee')
        : (isRtl ? 'إضافة محافظة جديدة' : 'Add New Governorate')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {isRtl ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : (isRtl ? 'حفظ' : 'Save')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {isEdit ? (
          <div className="form-group">
            <label className="form-label">{isRtl ? 'المحافظة' : 'Governorate'}</label>
            <div>{isRtl ? zone.nameAr : zone.nameEn}</div>
          </div>
        ) : (
          <>
            <Input
              label={isRtl ? 'الاسم (عربي)' : 'Name (Arabic)'}
              value={nameAr}
              onChange={(e) => setNameAr(e.target.value)}
              placeholder={isRtl ? 'مثال: الحلة' : 'e.g. Hillah'}
              required
            />
            <Input
              label={isRtl ? 'الاسم (إنجليزي)' : 'Name (English)'}
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder="e.g. Hillah"
              required
            />
          </>
        )}
        <Input
          label={isRtl ? 'أجور التوصيل (د.ع)' : 'Shipping Fee (IQD)'}
          type="number"
          min="0"
          step="250"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
        />
        {error && <div className="alert alert-danger">{error}</div>}
      </form>
    </Modal>
  );
};

export default ShippingZoneDialog;
